/**
 * Request Account - Email Entry Page
 *
 * First step in the account request flow.
 * User chooses a role (investor vs agent) then enters their email,
 * which is verified before proceeding.
 */

import { useState, useRef, type FormEvent, type KeyboardEvent } from 'react'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { Input } from '@/common/components/input'
import { Button } from '@/common/components/button'
import { Text } from '@/common/components/text'
import { verifyEmail, type RequestedRole } from '@/features/account-request/api/accountRequest'
import * as styles from './index.css'

export const Route = createFileRoute({
  validateSearch: (search: Record<string, unknown>): { role?: RequestedRole } => {
    const role = search.role
    if (role === 'investor' || role === 'agent') {
      return { role }
    }
    return {}
  },
  component: RequestAccountPage,
})

const ROLE_DESCRIPTIONS: Record<RequestedRole, string> = {
  investor: "Enter your email to join as an investor. We'll send you a verification link to continue.",
  agent: "Enter your email to join as an agent. We'll send you a verification link to continue.",
}

const ROLES: RequestedRole[] = ['investor', 'agent']

// Orchard employees auto-provision via the Google IDP path. Reject the email
// inline before the network call so they get an immediate, helpful message
// instead of the API's generic 400. Server-side check in post.verify-email.py
// is still the source of truth.
const ORCHARD_DOMAIN = '@orchard.com'
const ORCHARD_REJECTION_MESSAGE =
  'Orchard employees should sign in with their Orchard Google account.'

export function RequestAccountPage() {
  const navigate = useNavigate()
  // strict:false so unit tests (which render without a route context) don't crash.
  const search = useSearch({ strict: false }) as { role?: RequestedRole }
  const roleParam = search.role === 'agent' || search.role === 'investor' ? search.role : undefined
  const [role, setRole] = useState<RequestedRole>(roleParam ?? 'investor')
  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const radioRefs = useRef<(HTMLButtonElement | null)[]>([])

  const handleRoleKeyDown = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    let nextIndex: number | null = null
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      nextIndex = (index + 1) % ROLES.length
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      nextIndex = (index - 1 + ROLES.length) % ROLES.length
    } else if (e.key === 'Home') {
      nextIndex = 0
    } else if (e.key === 'End') {
      nextIndex = ROLES.length - 1
    }
    if (nextIndex !== null) {
      e.preventDefault()
      setRole(ROLES[nextIndex])
      radioRefs.current[nextIndex]?.focus()
    }
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)

    // Reject Orchard employees client-side before any network call. Same check
    // also enforced server-side; this is just for fast feedback + a clearer UX
    // (lets us render a "Sign in" link instead of a generic API error).
    if (email.toLowerCase().endsWith(ORCHARD_DOMAIN)) {
      setError(ORCHARD_REJECTION_MESSAGE)
      return
    }

    setIsSubmitting(true)
    try {
      await verifyEmail(email, role)
      navigate({ to: '/request-account/check-email' })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send verification email')
    } finally {
      setIsSubmitting(false)
    }
  }

  const isOrchardError = error === ORCHARD_REJECTION_MESSAGE

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.header}>
          <img
            src="/images/orchard-offers.svg"
            alt="Orchard Cash Offer"
            className={styles.logo}
          />
          <Text as="h1" variant="h4" weight="bold" className={styles.title}>
            Join the Marketplace
          </Text>
          <Text variant="body" className={styles.subtitle}>
            {ROLE_DESCRIPTIONS[role]}
          </Text>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div
            className={styles.roleToggle}
            role="radiogroup"
            aria-label="Account type"
          >
            <button
              type="button"
              role="radio"
              aria-checked={role === 'investor'}
              tabIndex={role === 'investor' ? 0 : -1}
              ref={(el) => { radioRefs.current[0] = el }}
              className={role === 'investor' ? styles.roleOptionSelected : styles.roleOption}
              onClick={() => setRole('investor')}
              onKeyDown={(e) => handleRoleKeyDown(e, 0)}
              disabled={isSubmitting}
            >
              I'm an investor
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={role === 'agent'}
              tabIndex={role === 'agent' ? 0 : -1}
              ref={(el) => { radioRefs.current[1] = el }}
              className={role === 'agent' ? styles.roleOptionSelected : styles.roleOption}
              onClick={() => setRole('agent')}
              onKeyDown={(e) => handleRoleKeyDown(e, 1)}
              disabled={isSubmitting}
            >
              I'm an agent
            </button>
          </div>

          {error && (
            <Text variant="bodySm" color="error" align="center" className={styles.error}>
              {error}
              {isOrchardError && (
                <>
                  {' '}
                  <Link to="/login" className={styles.primaryLink}>
                    Sign in
                  </Link>
                </>
              )}
            </Text>
          )}

          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isSubmitting}
            error={!!error}
            autoComplete="email"
          />

          <Button
            type="submit"
            width="full"
            disabled={isSubmitting || !email}
          >
            {isSubmitting ? 'Sending...' : 'Continue'}
          </Button>
        </form>

        <div className={styles.linksContainer}>
          <Link to="/login" className={styles.primaryLink}>
            Already have an account? Sign in
          </Link>
          <Link to="/" className={styles.mutedLink}>
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  )
}
